import { inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { IReleaseObjectGroupRoleViewModel, ReleaseObjectGroupRoleViewModel, ReleaseObjectGroupViewModel, ReleaseSelectedObject, ReleaseStatus } from '../models/autogenerated-piping';

/** Fresh state of a release object group (life cycle and review), sent to other tabs after a save or status change. */
export interface ReleaseLifecycleChange {
  releaseId: string;
  objectGroupId: string;
  action: ReleaseSelectedObject | undefined;
  /** Clone the release row should open; may differ from the previous one when a draft is restored. */
  newObjectId: string | undefined;
  hasPreservedDraft: boolean | undefined;
  reviewStatus: ReleaseObjectGroupViewModel['reviewStatus'];
  /** Per-role approval states; the release table derives its review status column from them. */
  roles: IReleaseObjectGroupRoleViewModel[] | undefined;
}

const LIFECYCLE_CHANNEL_NAME: string = 'release-object-lifecycle-changed';

/**
 * Release lifecycle (release as is / revise / retire) rules shared by the pipe class and valve
 * editors, plus the cross-tab channel that tells the release page when a save moved the lifecycle.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseLifecycleService implements OnDestroy {
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly incomingChanges: Subject<ReleaseLifecycleChange> = new Subject<ReleaseLifecycleChange>();
  private readonly channel: BroadcastChannel | null =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(LIFECYCLE_CHANNEL_NAME);

  /** Changes announced by other tabs. A tab never receives its own broadcasts. */
  readonly changes$: Observable<ReleaseLifecycleChange> = this.incomingChanges.asObservable();

  /** Latest announced change per object group id; a signal so templates and computeds react to it. */
  private readonly latestChangeByGroup: WritableSignal<ReadonlyMap<string, ReleaseLifecycleChange>> =
    signal<ReadonlyMap<string, ReleaseLifecycleChange>>(new Map<string, ReleaseLifecycleChange>());

  constructor() {
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<ReleaseLifecycleChange>) => {
        this.latestChangeByGroup.update((current: ReadonlyMap<string, ReleaseLifecycleChange>) =>
          new Map<string, ReleaseLifecycleChange>(current).set(event.data.objectGroupId, event.data));
        this.incomingChanges.next(event.data);
      };
    }
  }

  ngOnDestroy(): void {
    this.channel?.close();
  }

  /** Editor page URL for a release object, the same one the release page opens from a row. */
  getEditorUrl(objectType: 'Pipe' | 'Valve', objectId: string): string {
    return objectType === 'Valve'
      ? `/piping/valve/edit?mode=edit&id=${encodeURIComponent(objectId)}`
      : `/pipingsummary/${encodeURIComponent(objectId)}?isEditMode=true`;
  }

  /** True when the lifecycle badge and edit rules apply: the object belongs to a release that is not published yet. */
  isLifecycleApplicable(group: ReleaseObjectGroupViewModel | undefined | null): boolean {
    return !!group && group.releaseStatus !== undefined && group.releaseStatus !== ReleaseStatus.PUBLISHED;
  }

  /**
   * Why the editor must be read-only, or `null` when editing is allowed. Mirrors `ReleaseAccess.EnsureObjectEditableAsync`
   * on the server: retire is never editable, and release as is is editable only while the release is a draft.
   */
  getEditLockReason(group: ReleaseObjectGroupViewModel | undefined | null): string | null {
    if (!group || !this.isLifecycleApplicable(group)) { return null; }

    if (group.action === ReleaseSelectedObject._2) {
      return 'This item is set to retire in its release and cannot be edited. Change its life cycle to revise in the release first.';
    }
    if (group.action === ReleaseSelectedObject._0 && group.releaseStatus !== ReleaseStatus.DRAFT) {
      return 'This item is set to release as is and its release is no longer in draft, so it cannot be edited. '
        + 'Return the release to draft and change the life cycle to revise first.';
    }
    return null;
  }

  /** True when the editor must be read-only because of the release life cycle. */
  isEditLocked(group: ReleaseObjectGroupViewModel | undefined | null): boolean {
    return this.getEditLockReason(group) !== null;
  }

  /** True when saving must first move the object from "release as is" to "revise" (only possible in a draft release). */
  requiresReviseOnSave(group: ReleaseObjectGroupViewModel | undefined | null): boolean {
    return !!group
      && group.releaseStatus === ReleaseStatus.DRAFT
      && group.action === ReleaseSelectedObject._0;
  }

  /** Short label shown in the header badge. */
  getLabel(action: ReleaseSelectedObject | undefined): string {
    switch (action) {
      case ReleaseSelectedObject._1: return 'revise';
      case ReleaseSelectedObject._2: return 'retire';
      default: return 'release as is';
    }
  }

  /** Whether a save changed what the release page shows for this object. */
  hasLifecycleChanged(
    before: ReleaseObjectGroupViewModel | undefined | null,
    after: ReleaseObjectGroupViewModel | undefined | null
  ): boolean {
    if (!before || !after) { return false; }
    return before.action !== after.action
      || before.newObjectId !== after.newObjectId
      || before.hasPreservedDraft !== after.hasPreservedDraft;
  }

  /**
   * Asks the user to accept the move from "release as is" to "revise" that the save will perform.
   * @returns Emits `true` only on an affirmative answer; the save must not run otherwise.
   */
  confirmReviseOnSave(): Observable<boolean> {
    return this.dialog.open(ConfirmDialogComponent, {
      width: '480px',
      data: {
        dialogMessage: 'This item is set to "release as is". Saving your changes will change its life cycle to "revise". Do you wish to continue?',
        confirmText: 'save and change to revise',
        cancelText: 'cancel',
      },
    }).afterClosed().pipe(map((confirmed: boolean | undefined) => confirmed === true));
  }

  /** Latest change another tab announced for this group, if any. */
  getLatestChange(group: ReleaseObjectGroupViewModel | undefined | null): ReleaseLifecycleChange | undefined {
    return group?.id ? this.latestChangeByGroup().get(group.id) : undefined;
  }

  /** True when another tab changed this group's life cycle after the page loaded, so saving must be blocked. */
  isOutdated(group: ReleaseObjectGroupViewModel | undefined | null): boolean {
    return this.isOutdatedBy(group, this.getLatestChange(group));
  }

  /** Whether another tab announced a state for this group that differs from what the page loaded. */
  isOutdatedBy(group: ReleaseObjectGroupViewModel | undefined | null, change: ReleaseLifecycleChange | undefined): boolean {
    if (!group || !change || change.objectGroupId !== group.id) { return false; }
    return change.action !== group.action
      || change.newObjectId !== group.newObjectId
      || change.hasPreservedDraft !== group.hasPreservedDraft;
  }

  /**
   * Tells the other open tabs that a save changed the object's lifecycle state.
   * @param group Fresh group state from the server.
   * @param releaseId Owning release; defaults to the one embedded in `group` (pipe class / valve responses).
   */
  announceChange(group: ReleaseObjectGroupViewModel, releaseId: string | undefined = group.releaseId): void {
    if (!this.channel || !releaseId || !group.id) { return; }

    const change: ReleaseLifecycleChange = {
      releaseId,
      objectGroupId: group.id,
      action: group.action,
      newObjectId: group.newObjectId,
      hasPreservedDraft: group.hasPreservedDraft,
      reviewStatus: group.reviewStatus,
      roles: group.roles?.map((role: ReleaseObjectGroupRoleViewModel) => role.toJSON()),
    };
    this.channel.postMessage(change);
  }
}
